<template>
	<CurvesPage class="curve" @onScale="drawLine">
		
		<!-- Название кривой -->
		
		<template #curveName>Лемниската Бернулли</template>
		
		<!-- модуль Характеристика кривой -->
		
		<template #curveFeature>
			
			<h5 class="mt-0">
				Геометрическое место точек:
			</h5>
			<img src="@/assets/img/lemniscata_gmt.gif" class="feature__gmt">
			<p class="feature__gmt">
				<strong>Лемниската</strong>
				- геометрическое место точек, произведение расстояний от которых до двух заданных точек
				<vue-mathjax :formula="viewExpression('F_{1, 2}')" class="formula"></vue-mathjax> (фокусов) постоянно
				и равно квадрату половины расстояния между фокусами <vue-mathjax :formula="coefficientA" class="formula"></vue-mathjax>.
			</p>
			
			<!-- Уравнение в полярных координатах -->
			
			<h5 class="mt-0">
				Полярное уравнение:
				<b-badge variant="light" class="curve__main-formula">
					<vue-mathjax
							:formula="equationInPolar"
							class="formula"
					></vue-mathjax>
				</b-badge>
			</h5>
			
			<!-- Уравнение в декартовых координатах -->
			
			<h5>Аналитическое уравнение:</h5>
			<p>
				<vue-mathjax
						:formula="equationInCartesian"
						class="formula"
				></vue-mathjax>
			</p>
			
			<h5>Параметрическое уравнение:</h5>
			<p>
				<vue-mathjax
						:formula="equationParamInCartesian" class="formula"></vue-mathjax>
			</p>
			
			<!-- Область значений для параметра -->
			
			<h5 class="mt-0">Область значений <vue-mathjax :formula="parameterT" class="formula"></vue-mathjax>:</h5>
			
			<vue-slider
					v-model="t"
          :min="-100"
          :max="100"
          :interval="1"
          @dragging="drawLine"
			></vue-slider>
			<p>
				<vue-mathjax :formula="rangeODZ" class="formula"></vue-mathjax>
			</p>
			
			<!-- Значения коэффициентов -->
			
			<h5>Зависимость от коэффициентов:</h5>
			
			<!-- Коэффициент a -->
			
			<label>
				<vue-mathjax :formula="coefficientA" class="formula"></vue-mathjax>:
			</label>
			
			<b-form-spinbutton v-model="a" min="0" max="20" step="1" @input="drawLine" vertical
			                   class="curve__coef"></b-form-spinbutton>
			
		</template>
		
		<!-- модуль Информация о кривой -->
		
		<!-- Изображение для модуля информации -->
		
		<template #curvePhoto>
			<a href="https://ru.wikipedia.org/wiki/%D0%91%D0%B5%D1%80%D0%BD%D1%83%D0%BB%D0%BB%D0%B8,_%D0%AF%D0%BA%D0%BE%D0%B1"><img src="@/assets/img/lemniscata_info_bernoulli.jpg" width="100" alt="Якоб Бернулли"></a>
		</template>
		
		<!-- Контент -->
		
		<template #curveInfo>
			<h5>История:</h5>
			<p>
				Впервые лемниската была рассмотрена <i><b>Якобом Бернулли</b></i> (1654—1705) в 1694 г. Впоследствии Бернулли много часов
				своих занятий уделял лемнискате и нашел несколько ее интересных свойств.</p>
			<h5>Применение:</h5>
			<p>В технике лемниската используется, в частности, в качестве переходной кривой на закруглениях малого радиуса,
				как это имеет место на железнодорожных линиях в горной местности и на трамвайных путях. Таким образом она
				обеспечивает плавность закругления, без которой центробежная сила, действующая на поезд, возрастала бы резко,
				доставляя неудобство пассажирам.
			</p>
			<p>
				В качестве примера применения лемнискаты в области физики можно указать, что линия поля, создаваемого двумя
				параллельными токами, текущими по бесконечно длинным проводникам в плоскости, к ним перпендикулярной, является
				лемнискатой.
			</p>
		</template>
		
		<!-- модуль Задачи -->
		
		<template #curveTasks>
			<h5 class="mb-4">Примеры задач:</h5>
			<b-media>
				<template #aside>
					<h6>1)</h6>
				</template>
				<h6 class="mt-0">Вычислить площадь, заключённую внутри лемнискаты Бернулли <vue-mathjax :formula="viewExpression('r^{2} = a^{2} \\cos 2 \\theta ')" class="formula"></vue-mathjax></h6>
				<b-button v-b-toggle.answer-1 variant="dark">Показать решение</b-button>
				<b-collapse id="answer-1" class="mt-2 answer">
					<p>1) В полярной системе координат площадь фигуры, ограниченной кривой
						<vue-mathjax
								:formula="viewExpression(' r = f(\\theta) ')"
								class="formula"></vue-mathjax> и двумя полярными радиусами можно найти по формуле:
						<vue-mathjax :formula="viewExpression('S = \\frac {1}{2} \\int_{ α }^{ β } f_{\\theta}^{2} d\\theta')" class="formula"></vue-mathjax>;
					<br>
						2) В силу симметрии кривой определим четвёртую часть исходной площади:
					<vue-mathjax
							:formula="viewExpression('\\frac{1}{4}S = \\frac{1}{2}\\int_{0}^{\\frac{π}{4}}a^2cos(2\\theta )d\\theta  = \\frac{a^2}{2}\\cdot\\frac{1}{2}sin(2\\theta )\\bigg|_0^{\\frac{π}{4}} = \\frac{a^2}{4} ')"
							class="formula"
					></vue-mathjax> <br>
					 3) Тогда вся площадь будет равна:
						<vue-mathjax
								:formula="viewExpression(' S = a^{2} ')"
								class="formula"
						></vue-mathjax>
					</p>
				</b-collapse>
			</b-media>
		</template>
	
	</CurvesPage>
</template>

<script>
	import CurvesPage, { CurvesInterface } from '@/views/templates/CurvesPage';
	
	export default {
		name: 'LemniscataBernoulli',
		mixins: [CurvesInterface],
		data () {
			return {
				/* Коэффициенты уравнения */
				a: 0,
				/* ОДЗ параметра */
				t: [-10, 10]
			}
		},
		computed: {
			/* Функции для представления в latex
			 * rangeODZ - ОДЗ параметра
			 * equationInPolar - Уравнение в полярных координатах
			 * equationInCartesian - Уравнение в декартовых координатах
			 * equationParamInCartesian - Параметрическое уравнение
			  * */
			rangeODZ: (context) => {
				return '$$ ' + context.minTheta +  ' < t <' + context.maxTheta + '$$';
			},
			equationInPolar: () => {
				return '$$ r^{2}=2a^{2} \\cos2 \\theta $$';
			},
			equationInCartesian: () => {
				return '$$(x^{2} +  y^{2})^{2}= 2a^{2}(x^{2} - y^{2})$$';
			},
			equationParamInCartesian: () => {
				return '$$ \\begin{cases} x = a \\sqrt{2} \\frac{t+t^{3}}{1+t^{4}} \\\\ y = a \\sqrt{2} \\frac{t-t^{3}}{1+t^{4}} \\end{cases}$$';
			},
			/* Свойство ОДЗ */
			maxTheta: ({ t }) => {
				return t[1];
			},
			minTheta: ({ t }) => {
				return t[0];
			}
		},
		methods: {
			/**
			 * Возвращает значение x при заданном параметре
			 * @param {number} t
			 * @return {number}
			 */
			X (t) {
				return 160 + (this.a * Math.sqrt(2) * ((t + Math.pow(t,3)) / (1 + Math.pow(t,4)))) * this.$store.state.step;
			},
			/**
			 * Возвращает значение y при заданном параметре
			 * @param {number} t
			 * @return {number}
			 */
			Y (t) {
				return 160 + (this.a * Math.sqrt(2) * ((t - Math.pow(t,3)) / (1 + Math.pow(t,4)))) * this.$store.state.step;
			}
		},
		components: {
			CurvesPage
		}
	}
</script>

<style scoped lang="scss">

</style>
